<template>
  <div class="mobile-app-status-page">
    <div class="mobile-app-status-page-header">
      <div class="mobile-app-status-page-header__copy">
        <h1>Mobil Uygulama Durumu</h1>
        <p>
          Mobil uygulama kullanıcılarının güncel sürüm kullanımı, push erişimi
          ve iletişim durumu tek ekrandan takip edilir.
        </p>
      </div>
      <div class="mobile-app-status-page-header__actions">
        <v-btn
          outlined
          color="primary"
          small
          :loading="isLoading"
          @click="loadList"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Yenile
        </v-btn>
      </div>
    </div>

    <div class="mobile-context-chips">
      <span class="mobile-context-chip">
        Güncel sürüm:
        <strong>{{ summary.target_app_version || "-" }}</strong>
      </span>
      <span class="mobile-context-chip">
        Güncel build:
        <strong>{{ summary.target_build_number || "-" }}</strong>
      </span>
      <span class="mobile-context-chip">
        Son yenileme:
        <strong>{{ lastRefreshedText }}</strong>
      </span>
    </div>

    <div class="mobile-kpi-section-header">
      <div>
        <div class="mobile-kpi-section-title">
          Mobil uygulama kullanıcı durumu
        </div>
        <div class="mobile-kpi-section-caption">
          Üst kartlar mobil kullanıcı havuzunu, güncel sürüm kullanımını ve
          bildirim kanalı erişimini gösterir.
        </div>
      </div>
    </div>

    <div class="mobile-metric-grid">
      <article
        v-for="metric in healthMetrics"
        :key="metric.key"
        class="mobile-metric"
        :class="metric.class"
      >
        <div class="mobile-metric__topline">
          <div class="mobile-metric__label">{{ metric.label }}</div>
          <div
            class="mobile-metric__icon"
            :class="
              metric.iconTone && `mobile-metric__icon--${metric.iconTone}`
            "
          >
            <v-icon small>{{ metric.icon }}</v-icon>
          </div>
        </div>
        <div class="mobile-metric__value" :class="metric.valueClass">
          {{ metric.value }}
        </div>
        <div class="mobile-metric__caption">{{ metric.caption }}</div>
        <div
          class="mobile-metric__foot"
          :class="{ 'mobile-metric__foot--progress': metric.progress }"
        >
          <div class="mobile-metric__foot-row">
            <span>{{ metric.footLabel }}</span>
            <strong>{{ metric.footValue }}</strong>
          </div>
          <div
            v-if="metric.progress"
            class="mobile-metric__progress"
            :title="`${metric.footLabel}: ${metric.footValue}`"
          >
            <span :style="{ width: metric.progressWidth }"></span>
          </div>
        </div>
      </article>
    </div>

    <section class="mobile-operation-strip">
      <div class="mobile-operation-strip__header">
        <div>
          <div class="mobile-operation-strip__title">
            Kullanıcı durum segmentleri
          </div>
          <div class="mobile-operation-strip__subtitle">
            Segmentler tabloyu hızlı filtreler; sürüm, bildirim ve veri
            doğruluğu durumlarını ayrı ayrı incelenebilir hale getirir.
          </div>
        </div>
        <button
          type="button"
          class="mobile-operation-strip__clear"
          @click="clearSegment"
        >
          Filtreleri Temizle
        </button>
      </div>
      <div class="mobile-operation-items">
        <button
          v-for="segment in segmentItems"
          :key="segment.key"
          type="button"
          class="mobile-operation-item"
          :class="[
            `mobile-operation-item--${segment.tone}`,
            activeSegment === segment.key && 'mobile-operation-item--active',
          ]"
          @click="applySegment(segment.key)"
        >
          <span class="mobile-operation-item__icon">
            <v-icon small>{{ segment.icon }}</v-icon>
          </span>
          <span class="mobile-operation-item__copy">
            <span class="mobile-operation-item__title">
              {{ segment.title }}
            </span>
            <span class="mobile-operation-item__caption">
              {{ segment.caption }}
            </span>
            <span class="mobile-operation-item__link"> Listeyi görüntüle </span>
          </span>
          <span class="mobile-operation-item__count">
            {{ formatInteger(summary[segment.countKey]) }}
          </span>
        </button>
      </div>
    </section>

    <section class="mobile-app-status-panel">
      <div class="mobile-app-status-panel__header">
        <div>
          <div class="mobile-app-status-panel__title">Kullanıcı Detayı</div>
          <div class="mobile-app-status-panel__subtitle">
            Kullanıcı bazında güncel sürüm durumu, son raporlanan build, push
            erişimi ve veri kaynağı.
          </div>
        </div>
        <div class="mobile-table-filter-chips">
          <button
            v-for="segment in segmentItems"
            :key="`${segment.key}-chip`"
            type="button"
            class="mobile-table-filter-chip"
            :class="{
              'mobile-table-filter-chip--active': activeSegment === segment.key,
            }"
            @click="applySegment(segment.key)"
          >
            {{ segment.shortTitle }}
            <span>{{ formatInteger(summary[segment.countKey]) }}</span>
          </button>
        </div>
      </div>

      <v-card flat :loading="isLoading" class="mobile-app-status-table-card">
        <v-card-text class="pt-0">
          <v-data-table
            v-bind="dataTableAttrs"
            :headers="headersShown"
            :items="list"
            :loading="isLoading"
            :options.sync="options"
            :server-items-length="total"
            :items-per-page.sync="itemsPerPage"
            v-model="selectedItems"
            @update:items-per-page="handleItemsPerPageUpdated"
            :show-select="false"
            :item-class="getRowClass"
          >
            <template v-slot:top>
              <rs-table-title-bar
                v-if="$refs.headSearch"
                title="Kullanıcılar"
                icon="mdi-account-group"
                :add-route="null"
                @clear-filters="handleClearFilters"
                @reload="loadList"
                hide-edit
                :search.sync="search.query"
                v-bind="titleBarAttrs"
              />
            </template>

            <!-- eslint-disable-next-line -->
            <template v-slot:body.prepend="{ headers }">
              <rs-table-head-search
                :headers="headers"
                :search.sync="search"
                ref="headSearch"
              />
            </template>

            <!-- eslint-disable-next-line -->
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                title="Cihazları göster"
                @click="showDevices(item)"
              >
                <v-icon small>mdi-cellphone-information</v-icon>
              </v-btn>
            </template>

            <!-- eslint-disable-next-line -->
            <template v-slot:item.name="{ item }">
              <router-link
                :to="{ name: 'admin.users.edit', params: { id: item.user_id } }"
              >
                {{ item.name || "-" }}
              </router-link>
            </template>

            <!-- eslint-disable-next-line -->
            <template v-slot:item.phone="{ value }">
              <rs-table-cell-phone :value="value" />
            </template>

            <!-- eslint-disable-next-line -->
            <template v-slot:item.is_current="{ value }">
              <v-chip x-small dark :color="currentMeta(value).color">
                {{ currentMeta(value).text }}
              </v-chip>
            </template>

            <!-- eslint-disable-next-line -->
            <template v-slot:item.latest_app_version="{ item }">
              <span>{{ latestVersionText(item) }}</span>
            </template>

            <!-- eslint-disable-next-line -->
            <template v-slot:item.version_source="{ value }">
              <v-chip x-small dark :color="versionSourceMeta(value).color">
                {{ versionSourceMeta(value).text }}
              </v-chip>
            </template>

            <!-- eslint-disable-next-line -->
            <template v-slot:item.reachability="{ value }">
              <v-chip x-small dark :color="reachabilityMeta(value).color">
                {{ reachabilityMeta(value).text }}
              </v-chip>
            </template>

            <!-- eslint-disable-next-line -->
            <template v-slot:item.push_available="{ value }">
              <rs-table-cell-boolean :value="value" />
            </template>

            <!-- eslint-disable-next-line -->
            <template v-slot:item.sms_available="{ value }">
              <rs-table-cell-boolean :value="value" />
            </template>

            <!-- eslint-disable-next-line -->
            <template v-slot:item.last_seen_at="{ value }">
              <span>{{ formatDateTime(value) }}</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </section>

    <v-navigation-drawer
      v-model="deviceDrawer"
      temporary
      fixed
      right
      width="960"
      class="mobile-device-navigation-drawer"
    >
      <v-card flat class="mobile-device-drawer">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-cellphone-information</v-icon>
          <span>{{ selectedUserTitle }}</span>
          <v-spacer />
          <v-btn icon @click="deviceDrawer = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text>
          <v-alert v-if="devices.length === 0 && !devicesLoading" dense text>
            Kayıt bulunamadı.
          </v-alert>

          <div v-if="devices.length > 0" class="mobile-device-summary">
            <div
              v-for="item in deviceOverviewItems"
              :key="item.key"
              class="mobile-device-summary__item"
              :class="item.class"
            >
              <div class="mobile-device-summary__label">{{ item.label }}</div>
              <div class="mobile-device-summary__value">{{ item.value }}</div>
              <div class="mobile-device-summary__caption">
                {{ item.caption }}
              </div>
            </div>
          </div>

          <v-simple-table v-if="devices.length > 0" dense>
            <thead>
              <tr>
                <th>Durum</th>
                <th>Versiyon</th>
                <th>Push</th>
                <th>Görülme</th>
                <th>Kimlik</th>
                <th>Yorum</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="device in normalizedDevices"
                :key="device.rowKey"
                :class="device.rowClass"
              >
                <td>
                  <v-chip x-small dark :color="device.sourceMeta.color">
                    {{ device.sourceMeta.text }}
                  </v-chip>
                  <div class="mobile-device-table__muted">
                    {{ device.platform || "-" }}
                  </div>
                </td>
                <td>
                  <strong>{{ device.versionText }}</strong>
                  <div class="mobile-device-table__muted">
                    {{ device.versionSourceText }}
                  </div>
                </td>
                <td>
                  <v-chip
                    v-if="device.push_status"
                    x-small
                    dark
                    :color="device.pushMeta.color"
                  >
                    {{ device.pushMeta.text }}
                  </v-chip>
                  <span v-else>-</span>
                  <div class="mobile-device-table__muted">
                    {{ device.accessTokenText }}
                  </div>
                </td>
                <td>
                  <div>
                    <span class="mobile-device-table__label">İlk:</span>
                    {{ formatDateTime(device.first_seen_at) }}
                  </div>
                  <div>
                    <span class="mobile-device-table__label">Son:</span>
                    {{ formatDateTime(device.last_seen_at) }}
                  </div>
                </td>
                <td class="mobile-device-table__ids">
                  <v-chip x-small outlined :color="device.identityMeta.color">
                    {{ device.identityMeta.text }}
                  </v-chip>
                  <div>
                    <span class="mobile-device-table__label">
                      Installation:
                    </span>
                    {{ device.installation_id || "-" }}
                  </div>
                  <div>
                    <span class="mobile-device-table__label">OneSignal:</span>
                    {{ device.provider_player_id || "-" }}
                  </div>
                </td>
                <td>
                  <strong>{{ device.interpretation.title }}</strong>
                  <div class="mobile-device-table__muted">
                    {{ device.interpretation.caption }}
                  </div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { debounce } from "@/core/plugins/debounce";
import { filtersToURL } from "@/view/mixins";

export default {
  mixins: [filtersToURL],
  data() {
    return {
      summary: {},
      deviceDrawer: false,
      devicesLoading: false,
      selectedUser: null,
      devices: [],
      activeSegment: null,
      lastRefreshedAt: null,
      search: {
        query: this.$route.query.q ?? null,
        trigger: null,
      },
      options: {
        sortBy: ["last_seen_at"],
        sortDesc: [true],
      },
      dataTableAttrs: {
        fixedHeader: true,
        height: "auto",
        loadingText: "Yükleniyor",
        footerProps: {
          itemsPerPageOptions: [10, 20, 30, 50, 100, 200],
          disableItemsPerPage: false,
          showFirstLastPage: true,
        },
        itemKey: "user_id",
        dense: true,
        mobileBreakpoint: 0,
      },
      headers: [
        {
          text: "",
          value: "actions",
          searchable: false,
          sortable: false,
          width: "40px",
          align: "center",
        },
        {
          text: "User ID",
          value: "user_id",
          width: "90px",
        },
        {
          text: "Kullanıcı",
          value: "name",
          width: "220px",
        },
        {
          text: "Telefon",
          value: "phone",
          width: "140px",
        },
        {
          text: "Güncel mi?",
          value: "is_current",
          searchable: "yesNo",
          align: "center",
          width: "110px",
        },
        {
          text: "Platform",
          value: "latest_platform",
          searchable: "select",
          searchValue: "platform",
          options: () => this.platformOptions,
          itemText: "text",
          width: "110px",
        },
        {
          text: "Son Sürüm / Build",
          value: "latest_app_version",
          sortable: false,
          width: "180px",
        },
        {
          text: "Veri Kaynağı",
          value: "version_source",
          searchable: "select",
          options: () => this.versionSourceOptions,
          itemText: "text",
          width: "140px",
        },
        {
          text: "Ulaşım",
          value: "reachability",
          searchable: "select",
          options: () => this.reachabilityOptions,
          itemText: "text",
          width: "105px",
        },
        {
          text: "Push Açık",
          value: "push_available",
          searchable: "yesNo",
          sortable: false,
          align: "center",
          width: "95px",
        },
        {
          text: "SMS",
          value: "sms_available",
          searchable: "yesNo",
          sortable: false,
          align: "center",
          width: "80px",
        },
        {
          text: "Son Görülme",
          value: "last_seen_at",
          width: "160px",
        },
      ],
      titleBarAttrs: {},
      versionSourceOptions: [
        { id: "reported", text: "Cihazdan geldi" },
        { id: "unverified", text: "Doğrulanmadı" },
        { id: "unknown", text: "Bilinmiyor" },
      ],
      reachabilityOptions: [
        { id: "push", text: "Push" },
        { id: "sms", text: "SMS" },
        { id: "none", text: "Yok" },
      ],
      platformOptions: [
        { id: "ios", text: "iOS" },
        { id: "android", text: "Android" },
      ],
      pushStatusOptions: [
        { id: "active", text: "Açık" },
        { id: "opted_out", text: "Kapalı" },
        { id: "invalid", text: "Geçersiz" },
      ],
    };
  },
  computed: {
    healthMetrics() {
      return [
        {
          key: "total_mobile_users",
          label: "Mobil Kullanıcı",
          icon: "mdi-cellphone",
          class: "mobile-metric--primary",
          value: this.formatInteger(this.summary.total_mobile_users),
          caption: "App yüklü veya OneSignal push kaydı olan kullanıcı.",
          footLabel: "Tekilleştirme",
          footValue: "user_id",
        },
        {
          key: "current_users",
          label: "Güncel Sürüm",
          icon: "mdi-check",
          iconTone: "success",
          value: this.formatInteger(this.summary.current_users),
          valueClass: "mobile-metric__value--success",
          caption: "Güncel sürüm kullanan mobil kullanıcı.",
          footLabel: "Güncel kullanım oranı",
          footValue: this.currentUsageRateText,
          progress: true,
          progressWidth: this.currentUsageProgressWidth,
        },
        {
          key: "push_available_users",
          label: "Push Açık",
          icon: "mdi-bell-check",
          iconTone: "info",
          value: this.formatInteger(this.summary.push_available_users),
          caption: "Aktif push kanalı olan mobil kullanıcı.",
          footLabel: "Bildirim açık oranı",
          footValue: this.pushOpenRateText,
        },
        {
          key: "sms_available_users",
          label: "SMS Erişimi",
          icon: "mdi-message-text",
          value: this.formatInteger(this.summary.sms_available_users),
          caption: "Telefon numarası bulunan mobil kullanıcı.",
          footLabel: "SMS erişim oranı",
          footValue: this.smsAccessRateText,
        },
      ];
    },
    segmentItems() {
      return [
        {
          key: "not_current_push",
          title: "Güncel değil · push açık",
          shortTitle: "Push",
          caption: "Eski sürümde görünen ve aktif push kanalı olan kullanıcı.",
          countKey: "not_current_push_reachable_users",
          tone: "success",
          icon: "mdi-bell-check",
        },
        {
          key: "not_current_sms",
          title: "Güncel değil · SMS erişimi",
          shortTitle: "SMS",
          caption:
            "Eski sürümde görünen, aktif push kanalı olmayan ve telefon numarası bulunan kullanıcı.",
          countKey: "not_current_sms_reachable_users",
          tone: "info",
          icon: "mdi-message-text",
        },
        {
          key: "unverified",
          title: "Doğrulanmamış sürüm",
          shortTitle: "Doğrulanmadı",
          caption: "Backfill ile taşındı, cihaz henüz build raporlamadı.",
          countKey: "unverified_version_users",
          tone: "warning",
          icon: "mdi-help",
        },
        {
          key: "current",
          title: "Güncel raporlayan",
          shortTitle: "Güncel",
          caption: "Güncel uygulama sürümünden gerçek cihaz sinyali geldi.",
          countKey: "current_users",
          tone: "primary",
          icon: "mdi-check",
        },
      ];
    },
    selectedUserTitle() {
      if (!this.selectedUser) {
        return "Cihazlar";
      }

      return `${this.selectedUser.name || "Kullanıcı"} #${
        this.selectedUser.user_id
      }`;
    },
    normalizedDevices() {
      return this.devices.map((device, index) => {
        const sourceMeta = this.deviceSourceMeta(device.source);
        const pushMeta = this.pushStatusMeta(device.push_status);
        const identityMeta = this.deviceIdentityMeta(device);
        const interpretation = this.deviceInterpretation(device);

        return Object.assign({}, device, {
          rowKey: `${this.deviceKey(device)}-${index}`,
          rowClass: this.deviceRowClass(device),
          sourceMeta,
          pushMeta,
          identityMeta,
          interpretation,
          versionText: this.appBuildText(device),
          versionSourceText: this.deviceVersionSourceText(device),
          accessTokenText: this.deviceAccessTokenText(device),
        });
      });
    },
    realDevices() {
      return this.devices.filter((device) => this.isRealDevice(device));
    },
    baselineDevices() {
      return this.devices.filter((device) => this.isBaselineDevice(device));
    },
    currentRealDevices() {
      return this.realDevices.filter((device) => this.isCurrentDevice(device));
    },
    latestRealDevice() {
      return this.latestDevice(this.realDevices);
    },
    firstCurrentSeenAt() {
      const dates = this.currentRealDevices
        .map((device) => this.deviceFirstSeenValue(device))
        .filter((value) => value !== null)
        .sort((first, second) => first - second);

      if (dates.length === 0) {
        return null;
      }

      return new Date(dates[0]).toISOString();
    },
    activePushDeviceCount() {
      return this.devices.filter((device) => device.push_status === "active")
        .length;
    },
    deviceOverviewItems() {
      const latestReal = this.latestRealDevice;
      const hasCurrentReal = this.currentRealDevices.length > 0;
      const hasReal = this.realDevices.length > 0;
      const updateValue = hasCurrentReal
        ? "Güncel"
        : hasReal
        ? "Eski build"
        : "Doğrulanmadı";
      const updateCaption = hasCurrentReal
        ? `İlk güncel sinyal: ${this.formatDateTime(this.firstCurrentSeenAt)}`
        : hasReal
        ? `Son gerçek sinyal: ${this.appBuildText(latestReal)}`
        : "Sadece backfill veya push kaydı var";

      return [
        {
          key: "update",
          label: "Update durumu",
          value: updateValue,
          caption: updateCaption,
          class: hasCurrentReal
            ? "mobile-device-summary__item--success"
            : "mobile-device-summary__item--warning",
        },
        {
          key: "latest_real",
          label: "Son gerçek sürüm",
          value: latestReal ? this.appBuildText(latestReal) : "-",
          caption: latestReal
            ? this.formatDateTime(latestReal.last_seen_at)
            : "Gerçek bootstrap sinyali yok",
        },
        {
          key: "push",
          label: "Push",
          value: `${this.activePushDeviceCount} açık / ${this.devices.length} kayıt`,
          caption:
            this.activePushDeviceCount > 0
              ? "Aktif OneSignal kanalı var"
              : "Aktif push kanalı görünmüyor",
        },
        {
          key: "baseline",
          label: "Backfill",
          value: `${this.baselineDevices.length} kayıt`,
          caption: "Gerçek cihaz sinyali değil, OneSignal export geçmişi",
        },
      ];
    },
    currentUsageRate() {
      return this.percentage(
        this.summary.current_users,
        this.summary.total_mobile_users
      );
    },
    pushOpenRate() {
      return this.percentage(
        this.summary.push_available_users,
        this.summary.total_mobile_users
      );
    },
    smsAccessRate() {
      return this.percentage(
        this.summary.sms_available_users,
        this.summary.total_mobile_users
      );
    },
    currentUsageRateText() {
      return this.formatPercent(this.currentUsageRate);
    },
    pushOpenRateText() {
      return this.formatPercent(this.pushOpenRate);
    },
    smsAccessRateText() {
      return this.formatPercent(this.smsAccessRate);
    },
    currentUsageProgressWidth() {
      if (this.currentUsageRate <= 0) {
        return "0%";
      }

      return `${Math.max(this.currentUsageRate, 2)}%`;
    },
    lastRefreshedText() {
      if (!this.lastRefreshedAt) {
        return "-";
      }

      return this.lastRefreshedAt.toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  mounted() {
    this.clearDeprecatedFilters();
    this.syncActiveSegmentFromSearch();
    this.$nextTick(() => this.loadList());
  },
  methods: {
    getParams() {
      const params = Object.assign(
        this.buildSearchParams(this.search, this.options)
      );
      delete params.target_build_number;
      delete params.mobile_state;
      delete params.update_state;

      return params;
    },
    loadList() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.list = [];
      this.footTotals = [];

      const params = this.getParams();

      this.$api
        .query("api-super-admin/mobile-users", { params })
        .then((response) => {
          this.loadListFromResponse(response);
          this.loadSummary();
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.isLoading = false));
    },
    loadSummary(params = null) {
      this.$api
        .query("api-super-admin/mobile-users/summary", {
          params: params || {},
        })
        .then((response) => {
          this.summary = response.data.data || {};
          this.lastRefreshedAt = new Date();
        })
        .catch((error) => {
          this.handleError(error);
        });
    },
    handleClearFilters() {
      this.$refs.headSearch && this.$refs.headSearch.reset();
      this.clearDeprecatedFilters();
      this.clearSegment();
    },
    clearDeprecatedFilters() {
      this.$delete(this.search, "target_build_number");
      this.$delete(this.search, "mobile_state");
      this.$delete(this.search, "update_state");
    },
    segmentFilters(segmentKey) {
      const map = {
        not_current_push: {
          is_current: false,
          push_available: true,
        },
        not_current_sms: {
          is_current: false,
          push_available: false,
          sms_available: true,
        },
        unverified: {
          version_source: "unverified",
        },
        current: {
          is_current: true,
        },
      };

      return map[segmentKey] || {};
    },
    segmentFilterKeys() {
      return [
        "is_current",
        "push_available",
        "sms_available",
        "version_source",
      ];
    },
    applySegment(segmentKey) {
      const nextSearch = Object.assign({}, this.search);

      this.segmentFilterKeys().forEach((key) => {
        delete nextSearch[key];
      });

      Object.assign(nextSearch, this.segmentFilters(segmentKey));

      this.activeSegment = segmentKey;
      this.options.page = 1;
      this.search = nextSearch;
    },
    clearSegment() {
      const nextSearch = Object.assign({}, this.search);

      this.segmentFilterKeys().forEach((key) => {
        delete nextSearch[key];
      });

      this.activeSegment = null;
      this.options.page = 1;
      this.search = nextSearch;
    },
    syncActiveSegmentFromSearch() {
      const segment = this.segmentItems.find((item) => {
        const filters = this.segmentFilters(item.key);

        return Object.keys(filters).every(
          (key) => this.search[key] === filters[key]
        );
      });

      this.activeSegment = segment ? segment.key : null;
    },
    showDevices(item) {
      this.selectedUser = item;
      this.devices = [];
      this.deviceDrawer = true;
      this.devicesLoading = true;

      this.$api
        .query(`api-super-admin/mobile-users/${item.user_id}/devices`)
        .then((response) => {
          this.devices = response.data.data || [];
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.devicesLoading = false));
    },
    targetBuildNumber() {
      return Number(this.summary.target_build_number || 0);
    },
    deviceBuildNumber(device) {
      const value = device?.app_build_number || device?.app_build;

      if (value === null || value === undefined || value === "") {
        return null;
      }

      const numericValue = Number(value);

      return Number.isFinite(numericValue) ? numericValue : null;
    },
    isRealDevice(device) {
      return device?.source === "real_bootstrap";
    },
    isBaselineDevice(device) {
      return device?.source === "onesignal_baseline";
    },
    isCurrentDevice(device) {
      const targetBuild = this.targetBuildNumber();
      const buildNumber = this.deviceBuildNumber(device);

      if (
        !this.isRealDevice(device) ||
        targetBuild <= 0 ||
        buildNumber === null
      ) {
        return false;
      }

      return buildNumber >= targetBuild;
    },
    latestDevice(devices) {
      if (!devices.length) {
        return null;
      }

      return devices
        .slice()
        .sort(
          (first, second) =>
            this.deviceLastSeenValue(second) - this.deviceLastSeenValue(first)
        )[0];
    },
    deviceFirstSeenValue(device) {
      return this.dateValue(device?.first_seen_at || device?.last_seen_at);
    },
    deviceLastSeenValue(device) {
      return this.dateValue(device?.last_seen_at || device?.first_seen_at);
    },
    dateValue(value) {
      if (!value) {
        return null;
      }

      const timestamp = new Date(value).getTime();

      return Number.isNaN(timestamp) ? null : timestamp;
    },
    deviceVersionSourceText(device) {
      if (this.isRealDevice(device)) {
        return "cihazdan raporlandı";
      }

      if (this.isBaselineDevice(device)) {
        return "backfill varsayımı";
      }

      return device?.version_source || "-";
    },
    deviceAccessTokenText(device) {
      const value =
        device?.last_access_token_name || device?.last_access_token_id || null;

      return value ? `Token/Cihaz: ${value}` : "Token/Cihaz: -";
    },
    deviceIdentityMeta(device) {
      const installationId = device?.installation_id || null;
      const playerId = device?.provider_player_id || null;

      if (!installationId && playerId) {
        return { text: "Installation yok", color: "info" };
      }

      if (installationId && !playerId) {
        return { text: "Push yok", color: "grey" };
      }

      if (installationId && playerId && installationId === playerId) {
        return { text: "Backfill ID", color: "warning" };
      }

      return { text: "Gerçek installation", color: "success" };
    },
    deviceInterpretation(device) {
      if (this.isCurrentDevice(device)) {
        return {
          title: "Güncel sürüm doğrulandı",
          caption: "Hedef build veya üzeri gerçek bootstrap sinyali var.",
        };
      }

      if (this.isRealDevice(device)) {
        return {
          title: "Gerçek sinyal, hedef altında",
          caption:
            "Cihaz backend'e raporladı ancak build güncel hedefin altında.",
        };
      }

      if (this.isBaselineDevice(device)) {
        return {
          title: "Backfill kaydı",
          caption:
            "OneSignal export geçmişi; gerçek app bootstrap sinyali değil.",
        };
      }

      return {
        title: "Push kaydı",
        caption:
          "Installation bağlantısı olmayan OneSignal subscription kaydı.",
      };
    },
    deviceRowClass(device) {
      if (this.isCurrentDevice(device)) {
        return "mobile-device-row--current";
      }

      if (this.isBaselineDevice(device)) {
        return "mobile-device-row--baseline";
      }

      return "";
    },
    currentMeta(isCurrent) {
      return isCurrent
        ? { text: "Evet", color: "success" }
        : { text: "Hayır", color: "error" };
    },
    pushStatusMeta(status) {
      const map = {
        active: { text: "Açık", color: "success" },
        opted_out: { text: "Kapalı", color: "warning" },
        invalid: { text: "Geçersiz", color: "error" },
        revoked: { text: "İptal", color: "grey" },
      };

      return map[status] || { text: status || "-", color: "grey" };
    },
    deviceSourceMeta(source) {
      const map = {
        onesignal_baseline: { text: "Backfill", color: "warning" },
        real_bootstrap: { text: "Gerçek Cihaz", color: "success" },
        push_only: { text: "Push Kaydı", color: "info" },
      };

      return map[source] || { text: source || "-", color: "grey" };
    },
    versionSourceMeta(source) {
      const map = {
        reported: { text: "Cihazdan geldi", color: "success" },
        unverified: { text: "Doğrulanmadı", color: "warning" },
        unknown: { text: "Bilinmiyor", color: "grey" },
      };

      return map[source] || { text: source || "-", color: "grey" };
    },
    reachabilityMeta(reachability) {
      const map = {
        push: { text: "Push", color: "success" },
        sms: { text: "SMS", color: "info" },
        none: { text: "Yok", color: "grey" },
      };

      return map[reachability] || { text: reachability || "-", color: "grey" };
    },
    latestVersionText(item) {
      const version = item.latest_app_version || "-";
      const build =
        item.latest_app_build || item.latest_app_build_number || "-";

      return `${version} / ${build}`;
    },
    appBuildText(device) {
      const version = device.app_version || "-";
      const build = device.app_build || device.app_build_number || "-";

      return `${version} / ${build}`;
    },
    formatDateTime(value) {
      return value ? new Date(value).toLocaleString("tr-TR") : "-";
    },
    formatInteger(value) {
      return Number(value || 0).toLocaleString("tr-TR");
    },
    percentage(value, total) {
      const numericTotal = Number(total || 0);

      if (!numericTotal) {
        return 0;
      }

      return (Number(value || 0) / numericTotal) * 100;
    },
    formatPercent(value) {
      return `%${Number(value || 0).toLocaleString("tr-TR", {
        minimumFractionDigits: value > 0 && value < 1 ? 2 : 0,
        maximumFractionDigits: 2,
      })}`;
    },
    deviceKey(device) {
      return (
        device.push_subscription_id ||
        device.installation_id ||
        device.provider_player_id
      );
    },
    getRowClass(item) {
      if (!item.is_current) {
        return "mobile-user-row--old-build";
      }

      return "";
    },
  },
  watch: {
    options: {
      handler() {
        this.setURLParams();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.syncActiveSegmentFromSearch();
        this.options.page = 1;
        this.setURLParams();
      }, 250),
      deep: true,
    },
  },
};
</script>

<style scoped>
.mobile-app-status-page {
  background: #f0f1fa;
  margin: -12px;
  padding: 24px;
}

.mobile-app-status-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.mobile-app-status-page-header__copy {
  min-width: 0;
}

.mobile-app-status-page-header h1 {
  margin: 0 0 6px;
  color: #2b2d35;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
}

.mobile-app-status-page-header p {
  margin: 0;
  color: #a0a3b4;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
}

.mobile-app-status-page-header__actions {
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
}

.mobile-context-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.mobile-context-chip {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 4px 14px;
  border: 1px solid rgba(60, 60, 60, 0.12);
  border-radius: 999px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.54);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
}

.mobile-context-chip strong {
  margin-left: 4px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 800;
}

.mobile-kpi-section-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.mobile-kpi-section-title {
  color: rgba(0, 0, 0, 0.87);
  font-size: 15px;
  font-weight: 800;
  line-height: 1.25;
}

.mobile-kpi-section-caption {
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  line-height: 1.3;
}

.mobile-metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}

.mobile-metric {
  position: relative;
  overflow: hidden;
  min-height: 150px;
  padding: 16px 16px 44px;
  border: 1px solid #d9dbea;
  border-radius: 4px;
  background: #ffffff;
}

.mobile-metric--primary {
  border-color: rgba(88, 103, 221, 0.28);
  background: linear-gradient(135deg, rgba(88, 103, 221, 0.12), transparent 62%),
    #ffffff;
}

.mobile-metric__topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.mobile-metric__label {
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.2;
  text-transform: uppercase;
}

.mobile-metric__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  border-radius: 999px;
  background: #f4f6ff;
  color: #5867dd;
}

.mobile-metric__icon .v-icon {
  color: currentColor;
}

.mobile-metric__icon--success {
  background: #e8fff3;
  color: #1bc5bd;
}

.mobile-metric__icon--info {
  background: #eef7ff;
  color: #3699ff;
}

.mobile-metric__value {
  margin-top: 14px;
  color: rgba(0, 0, 0, 0.87);
  font-size: 36px;
  font-weight: 900;
  line-height: 1;
}

.mobile-metric__value--success {
  color: #1bc5bd;
}

.mobile-metric__caption {
  margin-top: 8px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
}

.mobile-metric__foot {
  position: absolute;
  right: 16px;
  bottom: 12px;
  left: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  font-weight: 800;
}

.mobile-metric__foot--progress {
  align-items: stretch;
  flex-direction: column;
  gap: 8px;
}

.mobile-metric__foot-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.mobile-metric__foot strong {
  color: #2b2d35;
}

.mobile-metric__progress {
  overflow: hidden;
  height: 8px;
  border-radius: 999px;
  background: #eef0f6;
}

.mobile-metric__progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #1bc5bd;
}

.mobile-operation-strip {
  margin-bottom: 18px;
  overflow: hidden;
  border: 1px solid #d9dbea;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(32, 33, 45, 0.1);
}

.mobile-operation-strip__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(60, 60, 60, 0.12);
}

.mobile-operation-strip__title {
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
  font-weight: 800;
}

.mobile-operation-strip__subtitle {
  margin-top: 3px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  font-weight: 600;
}

.mobile-operation-strip__clear {
  border: 0;
  background: transparent;
  color: #5867dd;
  cursor: pointer;
  font-family: inherit;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
  white-space: nowrap;
}

.mobile-operation-items {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.mobile-operation-item {
  position: relative;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  min-height: 110px;
  padding: 16px;
  border: 0;
  border-right: 1px solid rgba(60, 60, 60, 0.12);
  background: #ffffff;
  color: inherit;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: background-color 0.18s ease, box-shadow 0.18s ease;
}

.mobile-operation-item:last-child {
  border-right: 0;
}

.mobile-operation-item::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: #5867dd;
}

.mobile-operation-item:hover,
.mobile-operation-item:focus {
  outline: none;
  background: #fafbff;
}

.mobile-operation-item--active {
  background: #f7f8ff;
  box-shadow: inset 0 0 0 1px rgba(88, 103, 221, 0.18);
}

.mobile-operation-item--success::before {
  background: #1bc5bd;
}

.mobile-operation-item--info::before {
  background: #3699ff;
}

.mobile-operation-item--warning::before {
  background: #ffa800;
}

.mobile-operation-item__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: #f4f6ff;
  color: #5867dd;
}

.mobile-operation-item--success .mobile-operation-item__icon {
  background: #e8fff3;
  color: #1bc5bd;
}

.mobile-operation-item--info .mobile-operation-item__icon {
  background: #eef7ff;
  color: #3699ff;
}

.mobile-operation-item--warning .mobile-operation-item__icon {
  background: #fff8dd;
  color: #ffa800;
}

.mobile-operation-item__icon .v-icon {
  color: currentColor;
}

.mobile-operation-item__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.mobile-operation-item__title {
  color: rgba(0, 0, 0, 0.87);
  font-size: 13px;
  font-weight: 800;
  line-height: 1.25;
}

.mobile-operation-item__caption {
  margin-top: 5px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

.mobile-operation-item__link {
  margin-top: 8px;
  color: #3699ff;
  font-size: 12px;
  font-weight: 800;
}

.mobile-operation-item__count {
  color: rgba(0, 0, 0, 0.87);
  font-size: 26px;
  font-weight: 900;
  line-height: 1;
}

.mobile-app-status-panel {
  overflow: hidden;
  border: 1px solid #d9dbea;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(32, 33, 45, 0.1);
}

.mobile-app-status-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 26px 16px;
}

.mobile-app-status-panel__title {
  color: #2b2d35;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.25;
}

.mobile-app-status-panel__subtitle {
  margin-top: 5px;
  color: #7c7f8c;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
}

.mobile-table-filter-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.mobile-table-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
  padding: 0 14px;
  border: 1px solid rgba(60, 60, 60, 0.16);
  border-radius: 999px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.62);
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.mobile-table-filter-chip--active {
  border-color: #5867dd;
  background: #f7f8ff;
  color: #5867dd;
}

.mobile-table-filter-chip span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: #9b9b9b;
  color: #ffffff;
  font-size: 13px;
  font-weight: 900;
  line-height: 1;
}

.mobile-table-filter-chip--active span {
  background: #5867dd;
}

.mobile-app-status-table-card {
  background: transparent;
}

.mobile-app-status-table-card ::v-deep .v-card__text {
  padding: 0 18px 18px;
}

.mobile-app-status-table-card ::v-deep .v-data-table tbody > tr > td {
  height: auto;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

::v-deep .mobile-user-row--old-build {
  background: #fff5f5;
}

.mobile-device-drawer {
  min-height: 100%;
}

.mobile-device-navigation-drawer {
  z-index: 210;
}

.mobile-device-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.mobile-device-summary__item {
  min-height: 96px;
  padding: 12px;
  border: 1px solid #d9dbea;
  border-radius: 4px;
  background: #ffffff;
}

.mobile-device-summary__item--success {
  border-color: rgba(27, 197, 189, 0.35);
  background: #f2fffb;
}

.mobile-device-summary__item--warning {
  border-color: rgba(255, 168, 0, 0.35);
  background: #fffaf0;
}

.mobile-device-summary__label {
  color: rgba(0, 0, 0, 0.54);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.2;
  text-transform: uppercase;
}

.mobile-device-summary__value {
  margin-top: 8px;
  color: rgba(0, 0, 0, 0.87);
  font-size: 22px;
  font-weight: 900;
  line-height: 1.1;
}

.mobile-device-summary__caption {
  margin-top: 6px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
}

.mobile-device-table__muted {
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  line-height: 1.25;
}

.mobile-device-table__label {
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
  font-weight: 800;
}

.mobile-device-table__ids {
  max-width: 300px;
  word-break: break-all;
}

::v-deep .mobile-device-row--current {
  background: #f2fffb;
}

::v-deep .mobile-device-row--baseline {
  background: #fffaf0;
}

@media (max-width: 1260px) {
  .mobile-metric-grid,
  .mobile-operation-items,
  .mobile-device-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .mobile-app-status-page {
    margin: -8px;
    padding: 16px;
  }

  .mobile-app-status-page-header,
  .mobile-app-status-panel__header,
  .mobile-operation-strip__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .mobile-metric-grid,
  .mobile-operation-items,
  .mobile-device-summary {
    grid-template-columns: 1fr;
  }

  .mobile-operation-item {
    border-right: 0;
    border-bottom: 1px solid rgba(60, 60, 60, 0.12);
  }

  .mobile-operation-item:last-child {
    border-bottom: 0;
  }
}
</style>
